import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { Notification } from '~/shared/types/notification.js'
import type { OutgoingEmail } from '~/shared/types/outgoingEmail.js'
import { generateNotificationUrl } from '~/utils/emailRenderer.js'

export const useEmailStore = defineStore('emails', {
  state: () => ({
    loading: false,
    error: null as string | null,
    processingEmailId: null as string | null,
    isDevelopmentMode: process.env.NODE_ENV === 'development',
    devEmailAddress: useRuntimeConfig().public.dev_email || null,
  }),

  getters: {
    /**
     * Is the system currently processing an email
     */
    isProcessingEmail: state => state.processingEmailId !== null,
  },

  actions: {
    /**
     * Queue a new email to be sent
     */
    async queueEmail(params: {
      memberId: string // Changed from 'to' to 'memberId'
      subject?: string
      htmlContent?: string
      textContent?: string
      notificationId?: string
      type?: OutgoingEmail['type']
      metadata?: Record<string, unknown>
      template?: {
        name: string
        data: Record<string, unknown>
      }
    }): Promise<string | null> {
      try {
        this.loading = true
        this.error = null

        // Determine if we're using template or direct HTML content
        const useTemplate = params.template && params.template.name

        // In development mode, log the email info
        let subject = params.subject

        if (this.isDevelopmentMode) {
          // Prefix subject in development mode for clarity
          if (subject) {
            subject = `[DEV] ${subject}`
          }
          console.warn(`[DEV] Email would be sent to member ${params.memberId}`)
        }

        // Strongly-typed request body shapes
        interface BaseBody {
          memberId: string
          subject?: string
          notificationId?: string
          metadata: { type: OutgoingEmail['type'] } & Record<string, unknown>
        }
        type TemplateBody = BaseBody & { template: { name: string, data: Record<string, unknown> } }
        type HtmlBody = BaseBody & { html: string, text: string }
        type EmailRequestBody = TemplateBody | HtmlBody

        const baseBody: BaseBody = {
          memberId: params.memberId,
          metadata: {
            type: params.type || 'notification',
            ...(params.metadata ?? {}),
          },
        }

        // Add subject if provided
        if (subject) {
          baseBody.subject = subject
        }

        // Add notificationId if provided
        if (params.notificationId) {
          baseBody.notificationId = params.notificationId
        }

        // Build either template data or direct content
        let requestBody: EmailRequestBody
        if (useTemplate) {
          requestBody = { ...baseBody, template: params.template! }
        }
        else if (params.htmlContent) {
          requestBody = { ...baseBody, html: params.htmlContent, text: params.textContent || '' }
        }
        else {
          throw new Error('Either template or htmlContent must be provided')
        }

        // Call the server API endpoint to create an email in the mail collection
        const data = await $fetch('/api/emails/send-with-extension', {
          method: 'POST',
          body: requestBody,
        })

        // Check for errors
        if (!data || !data.success) {
          this.error = data?.message || 'Unknown error sending email'
          console.error('Error from email API:', this.error)
          return null
        }

        const emailId = data.id

        if (this.isDevelopmentMode) {
          console.warn(`[DEV] Email queued with ID: ${emailId}`)
        }

        return emailId
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : String(error)
        console.error('Error queueing email:', error)
        return null
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Queue an email for a notification
     */
    async queueNotificationEmail(
      notification: Notification,
      recipientEmail: string,
      recipientName: string,
      memberId: string,
    ): Promise<string | null> {
      try {
        this.loading = true
        this.error = null

        // Generate action URL from notification
        const actionUrl = generateNotificationUrl(notification)

        // Determine which template to use based on metadata or fallback to category
        const templateName = notification.metadata?.emailTemplate || notification.category || 'notification'

        // Prepare template data (variables that will be injected into the template)
        const templateData = {
          recipientName,
          title: notification.title,
          body: notification.body,
          actionUrl,
          actionText: notification.metadata?.actionText || 'View Details',
          ...(notification.metadata?.emailData ?? {}),
        }

        // Queue the email with Firebase Email Extension
        const data = await $fetch('/api/emails/send-with-extension', {
          method: 'POST',
          body: {
            memberId,
            subject: notification.title, // Still provide a subject for the email
            template: {
              name: templateName,
              data: templateData,
            },
            metadata: {
              notificationId: notification.id,
              notificationCategory: notification.category,
              notificationType: notification.metadata?.notificationType || 'general',
              memberId, // Explicitly include memberId in metadata
            },
          },
        })

        // Check for errors
        if (!data || !data.success) {
          this.error = data?.message || 'Unknown error sending email'
          console.error('Error from email API:', this.error)
          return null
        }

        return data.id
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : String(error)
        console.error('Error queueing notification email:', error)
        return null
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Get emails with optional filtering
     */
    async getEmails(options?: {
      status?: OutgoingEmail['status'] | 'sent' | 'failed' // Support legacy status values
      limit?: number
      type?: OutgoingEmail['type']
    }): Promise<OutgoingEmail[]> {
      try {
        this.loading = true
        this.error = null

        const fs = useFirestore()
        // Use the mail collection for Firebase Email Extension
        const emailsCollection = collection(fs, 'mail')

        // Note: Firebase Email Extension doesn't use the same fields as our OutgoingEmail type
        // We'll need to transform the data
        const q = query(emailsCollection, orderBy('metadata.createdAt', 'desc'))
        const querySnapshot = await getDocs(q)

        const emails: OutgoingEmail[] = []
        querySnapshot.forEach((doc) => {
          const data = doc.data()
          // Transform Firebase Email Extension document to our OutgoingEmail format
          emails.push({
            id: doc.id,
            to: data.to,
            subject: data.message?.subject || '',
            htmlContent: data.message?.html || '',
            textContent: data.message?.text || '',
            createdAt: data.metadata?.createdAt || new Date(),
            updatedAt: data.metadata?.createdAt || new Date(),

            status: this.mapFirebaseExtensionStatus(data.delivery?.state),
            attempts: data.delivery?.attempts || 0,
            maxAttempts: 3,
            sentAt: data.delivery?.deliveredAt,
            error: data.delivery?.error,
            notificationId: data.metadata?.notificationId,
            memberId: data.metadata?.memberId,
            type: data.metadata?.type || 'notification',
            metadata: data.metadata || {},
            originalTo: data.metadata?.originalTo,
          } as OutgoingEmail)
        })

        // Apply filters client-side since Firebase Email Extension has different schema
        let filteredEmails = emails

        if (options?.status) {
          filteredEmails = filteredEmails.filter((email) => {
            // Map legacy status values to new ones
            if (options.status === 'sent') return email.status === 'SUCCESS' || email.status === 'delivered'
            if (options.status === 'failed') return email.status === 'ERROR'
            return email.status === options.status
          })
        }

        if (options?.type) {
          filteredEmails = filteredEmails.filter(email => email.type === options.type)
        }

        if (options?.limit) {
          filteredEmails = filteredEmails.slice(0, options.limit)
        }

        return filteredEmails
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : String(error)
        console.error('Error fetching emails:', error)
        return []
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Map Firebase Email Extension status to our application status
     */
    mapFirebaseExtensionStatus(extensionStatus: string | undefined): OutgoingEmail['status'] {
      if (!extensionStatus) return 'pending'

      // Map Firebase Email Extension status to our status
      switch (extensionStatus) {
        case 'pending': return 'pending'
        case 'processing': return 'sending'
        case 'SUCCESS': return 'SUCCESS'
        case 'error': return 'ERROR'
        default: return 'pending'
      }
    },

    /**
     * Retry sending a failed email
     */
    async retryEmail(emailId: string): Promise<boolean> {
      try {
        this.loading = true
        this.error = null

        const fs = useFirestore()
        const emailRef = doc(fs, 'mail', emailId)
        const emailSnap = await getDoc(emailRef)

        if (!emailSnap.exists()) {
          throw new Error('Email not found')
        }

        const emailData = emailSnap.data()

        // Use the API endpoint to retry the email
        const data = await $fetch('/api/emails/send-with-extension', {
          method: 'POST',
          body: {
            to: emailData.to,
            subject: emailData.message?.subject,
            html: emailData.message?.html,
            text: emailData.message?.text,
            metadata: {
              ...emailData.metadata,
              retriedFrom: emailId,
              retriedAt: new Date(),
            },
          },
        })

        // Check for errors
        if (!data || !data.success) {
          throw new Error(data?.message || 'Unknown error retrying email')
        }

        if (this.isDevelopmentMode) {
          console.warn(`[DEV] Email ${emailId} retried with new ID: ${data.id}`)
        }

        return true
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : String(error)
        console.error('Error retrying email:', error)
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Cancel a pending email
     * Note: The Firebase Email Extension doesn't support cancellation directly,
     * but we can mark it as processed to prevent delivery
     */
    async cancelEmail(emailId: string): Promise<boolean> {
      try {
        this.loading = true
        this.error = null

        const fs = useFirestore()
        const emailRef = doc(fs, 'mail', emailId)
        const emailSnap = await getDoc(emailRef)

        if (!emailSnap.exists()) {
          throw new Error('Email not found')
        }

        // Add a cancellation marker to the metadata
        // This doesn't actually cancel the email if the extension is already processing it
        await updateDoc(emailRef, {
          'metadata.cancelled': true,
          'metadata.cancelledAt': serverTimestamp(),
        })

        if (this.isDevelopmentMode) {
          console.warn(`[DEV] Email ${emailId} marked as cancelled (note: this doesn't guarantee it won't be sent)`)
        }

        return true
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : String(error)
        console.error('Error cancelling email:', error)
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Resend an email by clearing the delivery map
     * This will cause the Firebase Email Extension to process the email again
     */
    async resendEmail(emailId: string): Promise<boolean> {
      try {
        this.loading = true
        this.error = null

        const fs = useFirestore()
        const emailRef = doc(fs, 'mail', emailId)
        const emailSnap = await getDoc(emailRef)

        if (!emailSnap.exists()) {
          throw new Error('Email not found')
        }

        // Remove the delivery field to make the Firebase Email Extension resend it
        await updateDoc(emailRef, {
          'delivery': null,
          'metadata.resent': true,
          'metadata.resentAt': serverTimestamp(),
          'metadata.previousStatus': emailSnap.data().delivery?.state || 'unknown',
        })

        if (this.isDevelopmentMode) {
          console.warn(`[DEV] Email ${emailId} marked for resending by clearing delivery map`)
        }

        return true
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : String(error)
        console.error('Error resending email:', error)
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Process all pending emails - not needed with Firebase Email Extension
     * as it processes emails automatically
     */
    async processAllPendingEmails(): Promise<boolean> {
      // With Firebase Email Extension, this is a no-op
      console.warn('Using Firebase Email Extension - emails are processed automatically')
      return true
    },
  },
})
